import Clarity from '@microsoft/clarity'

/**
 * Checks if Microsoft Clarity is enabled (production mode with valid project ID)
 */
export function isClarityEnabled(): boolean {
	return (
		import.meta.env.MODE === 'production' &&
		!!import.meta.env.VITE_CLARITY_PROJECT_ID
	)
}

/**
 * Identifies a user in Clarity for session attribution.
 * Only executes in production when Clarity is enabled.
 *
 * @param userId - Unique user identifier (e.g., Clerk user ID)
 * @param customSessionId - Optional custom session identifier
 * @param customPageId - Optional custom page identifier (e.g., route path)
 * @param friendlyName - Optional human-readable name for the user
 */
export function identifyUser(
	userId: string,
	customSessionId?: string,
	customPageId?: string,
	friendlyName?: string,
): void {
	if (!isClarityEnabled()) return
	Clarity.identify(userId, customSessionId, customPageId, friendlyName)
}

/**
 * Sets a tag on the current Clarity session for filtering/segmentation.
 * Only executes in production when Clarity is enabled.
 */
export function setTag(key: string, value: string): void {
	if (!isClarityEnabled()) return
	Clarity.setTag(key, value)
}

/**
 * Logs a custom event to Clarity for tracking user actions.
 * Only executes in production when Clarity is enabled.
 */
export function trackEvent(eventName: string): void {
	if (!isClarityEnabled()) return
	Clarity.event(eventName)
}

/**
 * Upgrades the current session priority (e.g., for error scenarios).
 * Only executes in production when Clarity is enabled.
 */
export function upgradeSession(reason: string): void {
	if (!isClarityEnabled()) return
	Clarity.upgrade(reason)
}
